<template>
    <div>
        <div v-if="label" class="text-xs text-muted-foreground mb-1">{{ label }}</div>
        <Select :model-value="String(modelValue)" @update:model-value="handleChange" :disabled="loading || disabled">
            <SelectTrigger :class="['min-w-0', triggerClass]" :style="{ width: width, maxWidth: maxWidth }">
                <SelectValue :placeholder="placeholder" />
            </SelectTrigger>
            <SelectContent>
                <SelectItem v-for="option in timeRangeOptions" :key="option.value" :value="String(option.value)" :disabled="option.disabled">
                    {{ option.label }}
                </SelectItem>
            </SelectContent>
        </Select>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import _ from 'lodash';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: 900 // 15 minutes in seconds
    },
    label: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: 'Select time range...'
    },
    loading: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    width: {
        type: String,
        default: 'auto'
    },
    maxWidth: {
        type: String,
        default: '14rem'
    },
    triggerClass: {
        type: String,
        default: ''
    },
    includeCustomRanges: {
        type: Boolean,
        default: false
    },
    includeLiveOption: {
        type: Boolean,
        default: true
    },
    includeAllTimeOption: {
        type: Boolean,
        default: false
    },
    includeDateOptions: {
        type: Boolean,
        default: false
    },
    hideOptions: {
        type: Array,
        default: () => []
    },
    includeShortRanges: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:modelValue']);

// Time-based ranges (in seconds)
const TIME_RANGES = {
    LIVE: 0,
    MINUTE: 60,
    FIFTEEN_MINUTES: 900,
    THIRTY_MINUTES: 1800,
    HOUR: 3600,
    SIX_HOURS: 21600,
    LAST_24H: 86400,
    LAST_7D: 604800,
    LAST_14D: 1209600,
    LAST_30D: 2592000,
    LAST_90D: 7776000,
    LAST_365D: 31536000
};

// Date-based ranges (string identifiers)
const DATE_RANGES = {
    ALL_TIME: 'allTime',
    TODAY: 'today',
    YESTERDAY: 'yesterday',
    THIS_WEEK: 'thisWeek',
    LAST_WEEK: 'lastWeek',
    THIS_MONTH: 'thisMonth',
    LAST_MONTH: 'lastMonth'
};

const timeRangeOptions = computed(() => {
    let allOptions = [];

    // Add Live option if enabled
    if (props.includeLiveOption) {
        allOptions.push({
            value: TIME_RANGES.LIVE,
            label: 'Live',
            disabled: true
        });
    }

    // Add short time ranges if enabled
    if (props.includeShortRanges && !props.includeDateOptions) {
        allOptions.push(
            { value: TIME_RANGES.MINUTE, label: 'Last minute' },
            { value: TIME_RANGES.FIFTEEN_MINUTES, label: 'Last 15 minutes' },
            { value: TIME_RANGES.THIRTY_MINUTES, label: 'Last 30 minutes' },
            { value: TIME_RANGES.HOUR, label: 'Last hour' },
            { value: TIME_RANGES.SIX_HOURS, label: 'Last 6 hours' }
        );
    }

    // Add standard time ranges
    allOptions.push(
        { value: TIME_RANGES.LAST_24H, label: 'Last 24 hours' },
        { value: TIME_RANGES.LAST_7D, label: 'Last 7 days' },
        { value: TIME_RANGES.LAST_14D, label: 'Last 14 days' },
        { value: TIME_RANGES.LAST_30D, label: 'Last 30 days' },
        { value: TIME_RANGES.LAST_90D, label: 'Last 90 days' },
        { value: TIME_RANGES.LAST_365D, label: 'Last 365 days' }
    );

    // Add date-based options if enabled
    if (props.includeDateOptions) {
        allOptions.push(
            { value: DATE_RANGES.TODAY, label: 'Today' },
            { value: DATE_RANGES.YESTERDAY, label: 'Yesterday' },
            { value: DATE_RANGES.THIS_WEEK, label: 'This Week' },
            { value: DATE_RANGES.LAST_WEEK, label: 'Last Week' },
            { value: DATE_RANGES.THIS_MONTH, label: 'This Month' },
            { value: DATE_RANGES.LAST_MONTH, label: 'Last Month' }
        );
    }

    // Add All Time option if enabled
    if (props.includeAllTimeOption) {
        allOptions.push({ value: DATE_RANGES.ALL_TIME, label: 'All Time' });
    }

    // Add custom range option if enabled
    if (props.includeCustomRanges) {
        allOptions.push({ value: -1, label: 'Custom range...', disabled: true });
    }

    // Filter out hidden options
    allOptions = allOptions.filter(option => !props.hideOptions.includes(option.value));

    // Sort: numeric values in ascending order, then string values
    return _.sortBy(allOptions, [
        (option) => typeof option.value === 'string' ? 1 : 0, // strings last
        (option) => typeof option.value === 'number' ? option.value : option.label // numeric by value, strings by label
    ]);
});

const handleChange = (value) => {
    // For date-based ranges, emit the string value directly
    // For time-based ranges, emit the numeric value
    if (typeof value === 'string' && isNaN(Number(value))) {
        emit('update:modelValue', value);
    } else {
        const numValue = Number(value);
        emit('update:modelValue', numValue);
    }
};
</script>
